import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../../services/authService';
import { Tag } from '../../types/auth';
import { exportPostsToCSV } from '../../utils/csvExport';
import './DCModuleDetailsPage.css';

const DCModuleDetailsPage: React.FC = () => {
  const { challengeId, moduleId } = useParams<{
    challengeId: string;
    moduleId: string;
  }>();
  const [searchParams] = useSearchParams();
  const moduleType = searchParams.get('type');
  const navigate = useNavigate();
  const [showPostModal, setShowPostModal] = useState(false);
  const [editingPost, setEditingPost] = useState<{ id: string } | null>(null);
  const [postForm, setPostForm] = useState({
    title: '',
    post_title: '',
    post_subtitle: '',
    share_file_url: '',
    text: '',
    script: '',
    first_question: '',
    second_question: '',
    question: '',
    image_url: '',
    link_url: '',
    thumbnail: '',
    video_url: '',
    youtube_url: '',
    audio_url: '',
    lrc_url: '',
    post_language: 'hi',
    post_type: '',
    category: '',
    tags: [] as string[],
    correct_answer_points: '',
    wrong_answer_points: '',
  });
  const [experimentScripts, setExperimentScripts] = useState<
    Array<{ script: string; bucket_list: string }>
  >([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [timeValue, setTimeValue] = useState('');
  const [mcqscData, setMcqscData] = useState({
    question: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctAnswer: '',
    explanation: '',
    correct_answer_points: '',
    wrong_answer_points: '',
  });
  const [mcqmcData, setMcqmcData] = useState({
    question: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctAnswers: { A: false, B: false, C: false, D: false },
    explanations: { A: '', B: '', C: '', D: '' },
    correct_answer_points: '',
    wrong_answer_points: '',
  });
  const [qnaData, setQnaData] = useState({
    question: '',
    answer: '',
    explanation: '',
  });

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await authService.getTags();
        setAvailableTags(response.tags || []);
      } catch (error) {
        console.error('Failed to fetch tags:', error);
      }
    };
    fetchTags();
  }, []);

  const handleAddPost = () => {
    setEditingPost(null);
    setShowPostModal(true);
    setPostForm({
      title: '',
      post_title: '',
      post_subtitle: '',
      share_file_url: '',
      text: '',
      script: '',
      first_question: '',
      second_question: '',
      question: '',
      image_url: '',
      link_url: '',
      thumbnail: '',
      video_url: '',
      youtube_url: '',
      audio_url: '',
      lrc_url: '',
      post_language: 'hi',
      post_type: '',
      category: '',
      tags: [],
      correct_answer_points: '',
      wrong_answer_points: '',
    });
    setExperimentScripts([]);
    setMcqscData({
      question: '',
      optionA: '',
      optionB: '',
      optionC: '',
      optionD: '',
      correctAnswer: '',
      explanation: '',
      correct_answer_points: '',
      wrong_answer_points: '',
    });
    setMcqmcData({
      question: '',
      optionA: '',
      optionB: '',
      optionC: '',
      optionD: '',
      correctAnswers: { A: false, B: false, C: false, D: false },
      explanations: { A: '', B: '', C: '', D: '' },
      correct_answer_points: '',
      wrong_answer_points: '',
    });
    setQnaData({
      question: '',
      answer: '',
      explanation: '',
    });
    setError(null);
    document.body.style.overflow = 'hidden';
  };

  const handleEditPost = (post: any) => {
    setEditingPost({ id: post.id });
    setPostForm({
      title: post.attributes?.title || '',
      post_title: post.post_title || '',
      post_subtitle: post.post_subtitle || '',
      share_file_url: post.share_file_url || '',
      text: post.text || '',
      script: post.script || '',
      first_question: post.first_question || '',
      second_question: post.second_question || '',
      question: post.question || '',
      image_url: post.image_url || '',
      link_url: post.link_url || '',
      thumbnail: post.thumbnail || '',
      video_url: post.video_url || '',
      youtube_url: post.youtube_url || '',
      audio_url: post.audio_url || '',
      lrc_url: post.lrc_url || '',
      post_language: post.post_language || 'hi',
      post_type: post.post_type || '',
      category: post.category || '',
      tags: post.tags || [],
      correct_answer_points: post.correct_answer_point || '',
      wrong_answer_points: post.wrong_answer_point || '',
    });

    if (post.experiment_script && Array.isArray(post.experiment_script)) {
      setExperimentScripts(
        post.experiment_script.map((exp: any) => ({
          script: exp.script || '',
          bucket_list: Array.isArray(exp.bucket_list)
            ? exp.bucket_list.join(', ')
            : '',
        }))
      );
    } else {
      setExperimentScripts([]);
    }

    if (post.post_type === 'mcqsc' && post.options) {
      setMcqscData({
        question: post.question || '',
        optionA: post.options[0]?.text || '',
        optionB: post.options[1]?.text || '',
        optionC: post.options[2]?.text || '',
        optionD: post.options[3]?.text || '',
        correctAnswer: post.answers?.[0]?.correct_answer || '',
        explanation: post.answers?.[0]?.explanation || '',
        correct_answer_points: post.correct_answer_points || '',
        wrong_answer_points: post.wrong_answer_points || '',
      });
    } else if (post.post_type === 'poll' && post.options) {
      setMcqscData({
        question: post.question || '',
        optionA: post.options[0]?.text || '',
        optionB: post.options[1]?.text || '',
        optionC: post.options[2]?.text || '',
        optionD: post.options[3]?.text || '',
        correctAnswer: '',
        explanation: '',
        correct_answer_points: post.correct_answer_points || '',
        wrong_answer_points: post.wrong_answer_points || '',
      });
    } else if (post.post_type === 'mcqmc' && post.options) {
      const correctAnswers = { A: false, B: false, C: false, D: false };
      const explanations = { A: '', B: '', C: '', D: '' };
      if (post.answers) {
        post.answers.forEach((ans: any) => {
          const option = ans.correct_answer as 'A' | 'B' | 'C' | 'D';
          correctAnswers[option] = true;
          explanations[option] = ans.explanation || '';
        });
      }
      setMcqmcData({
        question: post.question || '',
        optionA: post.options[0]?.text || '',
        optionB: post.options[1]?.text || '',
        optionC: post.options[2]?.text || '',
        optionD: post.options[3]?.text || '',
        correctAnswers,
        explanations,
        correct_answer_points: post.correct_answer_points || '',
        wrong_answer_points: post.wrong_answer_points || '',
      });
    } else if (post.post_type === 'qna') {
      setQnaData({
        question: post.question || '',
        answer: post.answers?.[0]?.correct_answer || '',
        explanation: post.answers?.[0]?.explanation || '',
      });
    }

    setShowPostModal(true);
    setError(null);
    document.body.style.overflow = 'hidden';
  };

  const handleClosePostModal = () => {
    setShowPostModal(false);
    setEditingPost(null);
    setPostForm({
      title: '',
      post_title: '',
      post_subtitle: '',
      share_file_url: '',
      text: '',
      script: '',
      first_question: '',
      second_question: '',
      question: '',
      image_url: '',
      link_url: '',
      thumbnail: '',
      video_url: '',
      youtube_url: '',
      audio_url: '',
      lrc_url: '',
      post_language: 'hi',
      post_type: '',
      category: '',
      tags: [],
      correct_answer_points: '',
      wrong_answer_points: '',
    });
    setExperimentScripts([]);
    setMcqscData({
      question: '',
      optionA: '',
      optionB: '',
      optionC: '',
      optionD: '',
      correctAnswer: '',
      explanation: '',
      correct_answer_points: '',
      wrong_answer_points: '',
    });
    setMcqmcData({
      question: '',
      optionA: '',
      optionB: '',
      optionC: '',
      optionD: '',
      correctAnswers: { A: false, B: false, C: false, D: false },
      explanations: { A: '', B: '', C: '', D: '' },
      correct_answer_points: '',
      wrong_answer_points: '',
    });
    setQnaData({
      question: '',
      answer: '',
      explanation: '',
    });
    setError(null);
    setSubmitting(false);
    document.body.style.overflow = 'unset';
  };

  const handleRemoveExperimentScript = (index: number) => {
    setExperimentScripts(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddExperimentScript = () => {
    setExperimentScripts(prev => [...prev, { script: '', bucket_list: '' }]);
  };

  const handleExperimentScriptChange = (
    index: number,
    field: string,
    value: string
  ) => {
    setExperimentScripts(prev =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const [posts, setPosts] = useState<any[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [sortByTitle, setSortByTitle] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [orderedPosts, setOrderedPosts] = useState<any[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderList, setOrderList] = useState<any[]>([]);
  const [orderDraggedIndex, setOrderDraggedIndex] = useState<number | null>(
    null
  );
  const [updatingPoints, setUpdatingPoints] = useState(false);

  useEffect(() => {
    if (moduleId) {
      fetchPosts();
    }
  }, [moduleId]);

  useEffect(() => {
    setOrderedPosts(posts);
  }, [posts]);

  const fetchPosts = async () => {
    try {
      setLoadingPosts(true);
      const response = await authService.getDCModulePosts(moduleId!);
      setPosts(response.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoadingPosts(false);
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    try {
      await authService.deleteDCModulePost(moduleId!, postId);
      await fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post. Please try again.');
    }
  };

  const handleDownloadCSV = () => {
    if (posts.length === 0) {
      alert('No posts available to download');
      return;
    }

    const filename = `daily_${challengeId}_module_${moduleId}.csv`;
    exportPostsToCSV(posts, filename);
  };

  const handleSaveOrder = async () => {
    try {
      const postIds = orderedPosts.map(post => post.id);
      await authService.batchUpdatePostOrder(postIds);
      await fetchPosts();
      alert('Order saved successfully!');
    } catch (error) {
      console.error('Error saving order:', error);
      alert('Failed to save order. Please try again.');
    }
  };

  const handleSubmitPost = async () => {
    try {
      setSubmitting(true);
      setError(null);

      if (!postForm.title.trim()) {
        setError('Title is required');
        return;
      }

      if (!postForm.post_type.trim()) {
        setError('Post type is required');
        return;
      }

      // Validate MCQSC specific fields
      if (postForm.post_type === 'mcqsc') {
        if (!mcqscData.question.trim()) {
          setError('Question is required for MCQSC');
          return;
        }
        if (!mcqscData.optionA.trim()) {
          setError('Option A is required for MCQSC');
          return;
        }
        if (!mcqscData.optionB.trim()) {
          setError('Option B is required for MCQSC');
          return;
        }
        if (!mcqscData.optionC.trim()) {
          setError('Option C is required for MCQSC');
          return;
        }
        if (!mcqscData.optionD.trim()) {
          setError('Option D is required for MCQSC');
          return;
        }
        if (!mcqscData.correctAnswer.trim()) {
          setError('Correct answer is required for MCQSC');
          return;
        }
        if (!mcqscData.explanation.trim()) {
          setError('Explanation is required for MCQSC');
          return;
        }
      }

      // Validate MCQMC specific fields
      if (postForm.post_type === 'mcqmc') {
        if (!mcqmcData.question.trim()) {
          setError('Question is required for MCQMC');
          return;
        }
        if (!mcqmcData.optionA.trim()) {
          setError('Option A is required for MCQMC');
          return;
        }
        if (!mcqmcData.optionB.trim()) {
          setError('Option B is required for MCQMC');
          return;
        }
        if (!mcqmcData.optionC.trim()) {
          setError('Option C is required for MCQMC');
          return;
        }
        if (!mcqmcData.optionD.trim()) {
          setError('Option D is required for MCQMC');
          return;
        }

        // Check if at least one correct answer is selected
        const hasSelectedAnswer = Object.values(mcqmcData.correctAnswers).some(
          selected => selected
        );
        if (!hasSelectedAnswer) {
          setError('At least one correct answer must be selected for MCQMC');
          return;
        }

        // Validate explanations for selected correct answers
        for (const [option, isSelected] of Object.entries(
          mcqmcData.correctAnswers
        )) {
          if (
            isSelected &&
            !mcqmcData.explanations[
              option as keyof typeof mcqmcData.explanations
            ].trim()
          ) {
            setError(`Explanation for option ${option} is required`);
            return;
          }
        }
      }

      // Validate Poll specific fields
      if (postForm.post_type === 'poll') {
        if (!mcqscData.question.trim()) {
          setError('Question is required for Poll');
          return;
        }
        if (!mcqscData.optionA.trim()) {
          setError('Option A is required for Poll');
          return;
        }
        if (!mcqscData.optionB.trim()) {
          setError('Option B is required for Poll');
          return;
        }
        if (!mcqscData.optionC.trim()) {
          setError('Option C is required for Poll');
          return;
        }
        if (!mcqscData.optionD.trim()) {
          setError('Option D is required for Poll');
          return;
        }
      }

      // Validate QNA specific fields
      if (postForm.post_type === 'qna') {
        if (!qnaData.question.trim()) {
          setError('Question is required for QNA');
          return;
        }
        if (!qnaData.answer.trim()) {
          setError('Answer is required for QNA');
          return;
        }
        if (!qnaData.explanation.trim()) {
          setError('Explanation is required for QNA');
          return;
        }
      }

      let options: any[] = [];
      let answers: any[] = [];
      let question = postForm.question;

      if (postForm.post_type === 'mcqsc') {
        question = mcqscData.question;
        options = [
          { text: mcqscData.optionA, option: 'A' },
          { text: mcqscData.optionB, option: 'B' },
          { text: mcqscData.optionC, option: 'C' },
          { text: mcqscData.optionD, option: 'D' },
        ];
        answers = [
          {
            explanation: mcqscData.explanation,
            correct_answer: mcqscData.correctAnswer,
          },
        ];
      } else if (postForm.post_type === 'poll') {
        question = mcqscData.question;
        options = [
          { text: mcqscData.optionA, option: 'A' },
          { text: mcqscData.optionB, option: 'B' },
          { text: mcqscData.optionC, option: 'C' },
          { text: mcqscData.optionD, option: 'D' },
        ];
        answers = [];
      } else if (postForm.post_type === 'mcqmc') {
        question = mcqmcData.question;
        options = [
          { text: mcqmcData.optionA, option: 'A' },
          { text: mcqmcData.optionB, option: 'B' },
          { text: mcqmcData.optionC, option: 'C' },
          { text: mcqmcData.optionD, option: 'D' },
        ];
        answers = Object.entries(mcqmcData.correctAnswers)
          .filter(([, isSelected]) => isSelected)
          .map(([option]) => ({
            explanation:
              mcqmcData.explanations[
                option as keyof typeof mcqmcData.explanations
              ],
            correct_answer: option,
          }));
      } else if (postForm.post_type === 'qna') {
        question = qnaData.question;
        answers = [
          {
            correct_answer: qnaData.answer,
            explanation: qnaData.explanation,
          },
        ];
      }

      const postData: any = {
        attributes: { title: postForm.title },
        text: postForm.text,
        script: postForm.script,
        experiment_script: experimentScripts
          .filter(exp => exp.script.trim() && exp.bucket_list.trim())
          .map(exp => ({
            script: exp.script,
            bucket_list: exp.bucket_list
              .split(',')
              .map(n => parseInt(n.trim()))
              .filter(n => !isNaN(n)),
          })),
        first_question: postForm.first_question,
        second_question: postForm.second_question,
        question,
        options,
        answers,
        image_url: postForm.image_url,
        link_url: postForm.link_url,
        thumbnail: postForm.thumbnail,
        video_url: postForm.video_url,
        youtube_url: postForm.youtube_url,
        audio_url: postForm.audio_url,
        lrc_url: postForm.lrc_url,
        post_language: postForm.post_language,
        post_type: postForm.post_type,
        category: postForm.category,
        tags: postForm.tags,
        user_id: '',
      };

      if (
        postForm.correct_answer_points &&
        String(postForm.correct_answer_points).trim()
      ) {
        postData.correct_answer_point = postForm.correct_answer_points;
      } else {
        postData.correct_answer_point = null;
      }
      if (
        postForm.wrong_answer_points &&
        String(postForm.wrong_answer_points).trim()
      ) {
        postData.wrong_answer_point = postForm.wrong_answer_points;
      } else {
        postData.wrong_answer_point = null;
      }

      if (editingPost) {
        await authService.updateDCChallengePost(editingPost.id, postData);
        console.log('Post updated');
      } else {
        const createdPost = await authService.createDCChallengePost(postData);
        console.log('Post created:', createdPost);

        await authService.createDCModulePostMapper({
          module_id: moduleId!,
          post_id: createdPost.id!,
          entity_id: challengeId!,
        });

        console.log('Mapper API called');
      }

      await fetchPosts();

      document.body.style.overflow = 'unset';
      handleClosePostModal();
    } catch (error) {
      console.error(
        `Error ${editingPost ? 'updating' : 'creating'} post:`,
        error
      );
      setError(
        `Failed to ${editingPost ? 'update' : 'create'} post. Please try again.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleMouseDown = (e: React.MouseEvent, index: number) => {
    if ((e.target as HTMLElement).closest('.card-actions, .clock-icon')) return;

    const startX = e.clientX;
    const startY = e.clientY;
    let hasDragStarted = false;
    let dragDirection: 'horizontal' | 'vertical' | null = null;
    const originalIndex = index;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      if (!hasDragStarted && distance < 10) return;

      if (!hasDragStarted) {
        hasDragStarted = true;
        setIsDragging(true);
        setDraggedIndex(originalIndex);
        document.body.classList.add('dragging');
        dragDirection =
          Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
      }

      const x = dragDirection === 'horizontal' ? deltaX : 0;
      const y = dragDirection === 'vertical' ? deltaY : 0;
      setDragOffset({ x, y });

      let newIndex = originalIndex;

      if (dragDirection === 'horizontal') {
        const swapThreshold = 185;
        const swapCount = Math.floor(Math.abs(deltaX) / swapThreshold);

        if (deltaX > 0) {
          newIndex = Math.min(
            originalIndex + swapCount,
            orderedPosts.length - 1
          );
        } else if (deltaX < 0) {
          newIndex = Math.max(originalIndex - swapCount, 0);
        }
      } else if (dragDirection === 'vertical') {
        const container = document.querySelector(
          '.modules-list'
        ) as HTMLElement;
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const cardWidth = 370;
          const cols = Math.floor(containerRect.width / cardWidth);
          const swapThreshold = 100;
          const rowCount = Math.floor(Math.abs(deltaY) / swapThreshold);
          const originalRow = Math.floor(originalIndex / cols);
          const originalCol = originalIndex % cols;

          if (deltaY > 0 && rowCount > 0) {
            const targetRow = originalRow + rowCount;
            const targetIndex = targetRow * cols + originalCol;
            if (targetIndex < orderedPosts.length) {
              newIndex = targetIndex;
            }
          } else if (deltaY < 0 && rowCount > 0) {
            const targetRow = originalRow - rowCount;
            if (targetRow >= 0) {
              const targetIndex = targetRow * cols + originalCol;
              newIndex = targetIndex;
            }
          }
        }
      }

      if (newIndex !== originalIndex) {
        const newOrder = [...orderedPosts];
        // Insert for both horizontal and vertical movement
        const draggedItem = newOrder[originalIndex];
        newOrder.splice(originalIndex, 1);
        newOrder.splice(newIndex, 0, draggedItem);
        setOrderedPosts(newOrder);
        setDraggedIndex(newIndex);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setDraggedIndex(null);
      setDragOffset({ x: 0, y: 0 });
      document.body.classList.remove('dragging');
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleUpdateModulePoints = async () => {
    try {
      setUpdatingPoints(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/daily-module/${moduleId}/update-module-points`,
        {
          method: 'PATCH',
          headers: {
            accept: 'application/json',
            ...(token && { 'x-auth-token': token }),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update module points');
      }

      alert('Module points updated successfully!');
      await fetchPosts();
    } catch (error) {
      console.error('Error updating module points:', error);
      alert('Failed to update module points. Please try again.');
    } finally {
      setUpdatingPoints(false);
    }
  };

  return (
    <div className="dc-module-details-page">
      <div className="top-strip"></div>

      <div className="page-header">
        <div className="header-info">
          <h1>Module Details</h1>
          <p>
            <span
              onClick={() => navigator.clipboard.writeText(moduleId || '')}
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              title="Click to copy Module ID"
            >
              Module ID: {moduleId}
            </span>{' '}
            | Type: {moduleType}
          </p>
        </div>
      </div>

      <div className="page-content">
        <div className="content-header">
          <h2>Posts</h2>
          <div className="header-actions">
            <button
              className={`toggle-button ${sortByTitle ? 'active' : ''}`}
              onClick={() => setSortByTitle(!sortByTitle)}
            >
              Sort by Title
            </button>
            <button
              className="toggle-button"
              onClick={handleUpdateModulePoints}
              disabled={updatingPoints}
            >
              {updatingPoints ? 'Updating...' : 'Update Module Points'}
            </button>
            <button
              className="toggle-button"
              onClick={() => {
                setOrderList([...orderedPosts]);
                setShowOrderModal(true);
              }}
            >
              Change Order
            </button>
            <button
              className={`toggle-button ${showDuplicates ? 'active' : ''}`}
              onClick={() => setShowDuplicates(!showDuplicates)}
            >
              Show Duplicates
            </button>
            <button className="download-csv-button" onClick={handleDownloadCSV}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7,10 12,15 17,10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download as CSV
            </button>
            <button className="create-button" onClick={handleAddPost}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Post
            </button>
          </div>
        </div>

        {loadingPosts ? (
          <div className="empty-state">
            <div className="button-spinner"></div>
            <p>Loading posts...</p>
          </div>
        ) : orderedPosts.length > 0 ? (
          <div className="modules-list">
            {(() => {
              let filteredPosts = orderedPosts;

              if (showDuplicates) {
                const titleCounts = orderedPosts.reduce(
                  (acc, post) => {
                    const title = post.attributes?.title || '';
                    acc[title] = (acc[title] || 0) + 1;
                    return acc;
                  },
                  {} as Record<string, number>
                );

                filteredPosts = orderedPosts.filter(post => {
                  const title = post.attributes?.title || '';
                  return titleCounts[title] > 1;
                });
              }

              if (sortByTitle) {
                filteredPosts = [...filteredPosts].sort((a, b) =>
                  (a.attributes?.title || '').localeCompare(
                    b.attributes?.title || ''
                  )
                );
              }

              return filteredPosts.filter(post => post && post.id);
            })().map((post, index) => (
              <div
                key={post?.id || index}
                className="module-card"
                data-post-id={post?.id || ''}
              >
                <div className="post-rank">#{index + 1}</div>
                <div className="module-card-header">
                  <h3>{post?.attributes?.title || 'Untitled'}</h3>
                  <div className="card-actions">
                    <svg
                      className="clock-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      onClick={e => {
                        e.stopPropagation();
                        setSelectedPost(post);
                        setTimeValue(post.created_at);
                        setShowTimeModal(true);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12,6 12,12 16,14" />
                    </svg>
                    <button
                      className="edit-module-button"
                      onClick={e => {
                        e.stopPropagation();
                        handleEditPost(post);
                      }}
                      title="Edit post"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      className="delete-module-button"
                      onClick={e => {
                        e.stopPropagation();
                        handleDeletePost(post?.id || '');
                      }}
                      title="Delete post"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="3,6 5,6 21,6" />
                        <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="module-card-body">
                  <div className="module-info">
                    <p>
                      <strong>ID:</strong> {post?.id || 'N/A'}{' '}
                      <svg
                        onClick={() =>
                          navigator.clipboard.writeText(post?.id || '')
                        }
                        style={{ cursor: 'pointer' }}
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <title>Click to copy Post ID</title>
                        <rect
                          x="9"
                          y="9"
                          width="13"
                          height="13"
                          rx="2"
                          ry="2"
                        />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    </p>
                    <p>
                      <strong>Type:</strong> {post?.post_type || 'N/A'}
                    </p>
                    <p>
                      <strong>Language:</strong> {post?.post_language || 'N/A'}
                    </p>
                    <p>
                      <strong>Created:</strong>{' '}
                      {post?.created_at
                        ? new Date(post.created_at).toLocaleString()
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <svg
              width="60"
              height="60"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21,15 16,10 5,21" />
            </svg>
            <h3>No posts yet</h3>
            <p>Add your first post to get started</p>
          </div>
        )}
      </div>

      {showPostModal && (
        <div className="modal-overlay" onClick={handleClosePostModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingPost ? 'Edit Post' : 'Add Post'}</h2>
              <button className="modal-close" onClick={handleClosePostModal}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {error && (
                <div className="modal-error">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {error}
                </div>
              )}
              <div className="form-group">
                <label htmlFor="title">
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  value={postForm.title}
                  onChange={e =>
                    setPostForm({ ...postForm, title: e.target.value })
                  }
                  placeholder="Enter post title"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="post_title">Post Title</label>
                <input
                  type="text"
                  id="post_title"
                  value={postForm.post_title}
                  onChange={e =>
                    setPostForm({ ...postForm, post_title: e.target.value })
                  }
                  placeholder="Enter post title"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="post_subtitle">Post Subtitle</label>
                <input
                  type="text"
                  id="post_subtitle"
                  value={postForm.post_subtitle}
                  onChange={e =>
                    setPostForm({ ...postForm, post_subtitle: e.target.value })
                  }
                  placeholder="Enter post subtitle"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="share_file_url">Share File URL</label>
                <input
                  type="text"
                  id="share_file_url"
                  value={postForm.share_file_url}
                  className="form-input"
                  readOnly
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  placeholder={
                    postForm.share_file_url ? '' : 'No share file URL available'
                  }
                />
              </div>
              <div className="form-group">
                <label htmlFor="post_type">
                  Post Type <span className="required">*</span>
                </label>
                <select
                  id="post_type"
                  value={postForm.post_type}
                  onChange={e =>
                    setPostForm({ ...postForm, post_type: e.target.value })
                  }
                  className="form-input"
                >
                  <option value="">Select post type</option>
                  <option value="video">Video</option>
                  <option value="audio">Audio</option>
                  <option value="html">HTML</option>
                  <option value="image">Image</option>
                  <option value="mcqsc">MCQSC</option>
                  <option value="mcqmc">MCQMC</option>
                  <option value="qna">QNA</option>
                  <option value="poll">Poll</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="post_text">Text</label>
                <textarea
                  id="post_text"
                  value={postForm.text}
                  onChange={e =>
                    setPostForm({ ...postForm, text: e.target.value })
                  }
                  placeholder="Enter post text"
                  className="form-input"
                  rows={4}
                />
              </div>
              <div className="form-group">
                <label htmlFor="script">Script</label>
                <textarea
                  id="script"
                  value={postForm.script}
                  onChange={e =>
                    setPostForm({ ...postForm, script: e.target.value })
                  }
                  placeholder="Enter script"
                  className="form-input"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Experiment Script</label>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleAddExperimentScript}
                  style={{
                    backgroundColor: '#28a745',
                    borderColor: '#28a745',
                    color: 'white',
                    marginTop: '10px',
                  }}
                >
                  + Add Experiment Script
                </button>
              </div>

              {experimentScripts.map((experiment, index) => (
                <div
                  key={index}
                  style={{
                    border: '1px solid #ddd',
                    padding: '15px',
                    marginBottom: '15px',
                    borderRadius: '5px',
                    position: 'relative',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => handleRemoveExperimentScript(index)}
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      backgroundColor: '#dc3545',
                      borderColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      padding: '5px 10px',
                      cursor: 'pointer',
                    }}
                  >
                    ×
                  </button>
                  <div className="form-group">
                    <label>Script Text</label>
                    <textarea
                      className="form-input"
                      value={experiment.script}
                      onChange={e =>
                        handleExperimentScriptChange(
                          index,
                          'script',
                          e.target.value
                        )
                      }
                      placeholder="Enter script text"
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>Bucket List (comma-separated numbers)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={experiment.bucket_list}
                      onChange={e =>
                        handleExperimentScriptChange(
                          index,
                          'bucket_list',
                          e.target.value
                        )
                      }
                      placeholder="e.g., 1, 2, 3"
                    />
                  </div>
                </div>
              ))}

              <div className="form-group">
                <label htmlFor="first_question">First Question</label>
                <input
                  type="text"
                  id="first_question"
                  value={postForm.first_question}
                  onChange={e =>
                    setPostForm({ ...postForm, first_question: e.target.value })
                  }
                  placeholder="Enter first question"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="second_question">Second Question</label>
                <input
                  type="text"
                  id="second_question"
                  value={postForm.second_question}
                  onChange={e =>
                    setPostForm({
                      ...postForm,
                      second_question: e.target.value,
                    })
                  }
                  placeholder="Enter second question"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="question">Question</label>
                <input
                  type="text"
                  id="question"
                  value={postForm.question}
                  onChange={e =>
                    setPostForm({ ...postForm, question: e.target.value })
                  }
                  placeholder="Enter question"
                  className="form-input"
                />
              </div>

              {postForm.post_type === 'mcqsc' && (
                <div className="mcqsc-section">
                  <h3 className="section-title">MCQSC Question Details</h3>
                  <div className="form-group">
                    <label>
                      Question <span className="required">*</span>
                    </label>
                    <textarea
                      value={mcqscData.question}
                      onChange={e =>
                        setMcqscData({ ...mcqscData, question: e.target.value })
                      }
                      className="form-input"
                      rows={3}
                    />
                  </div>
                  <div className="options-grid">
                    <div className="form-group">
                      <label>
                        Option A <span className="required">*</span>
                      </label>
                      <textarea
                        value={mcqscData.optionA}
                        onChange={e =>
                          setMcqscData({
                            ...mcqscData,
                            optionA: e.target.value,
                          })
                        }
                        className="form-input"
                        rows={2}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Option B <span className="required">*</span>
                      </label>
                      <textarea
                        value={mcqscData.optionB}
                        onChange={e =>
                          setMcqscData({
                            ...mcqscData,
                            optionB: e.target.value,
                          })
                        }
                        className="form-input"
                        rows={2}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Option C <span className="required">*</span>
                      </label>
                      <textarea
                        value={mcqscData.optionC}
                        onChange={e =>
                          setMcqscData({
                            ...mcqscData,
                            optionC: e.target.value,
                          })
                        }
                        className="form-input"
                        rows={2}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Option D <span className="required">*</span>
                      </label>
                      <textarea
                        value={mcqscData.optionD}
                        onChange={e =>
                          setMcqscData({
                            ...mcqscData,
                            optionD: e.target.value,
                          })
                        }
                        className="form-input"
                        rows={2}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        Correct Answer <span className="required">*</span>
                      </label>
                      <select
                        value={mcqscData.correctAnswer}
                        onChange={e =>
                          setMcqscData({
                            ...mcqscData,
                            correctAnswer: e.target.value,
                          })
                        }
                        className="form-input"
                      >
                        <option value="">Select correct answer</option>
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                        <option value="D">D</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>
                        Explanation <span className="required">*</span>
                      </label>
                      <textarea
                        value={mcqscData.explanation}
                        onChange={e =>
                          setMcqscData({
                            ...mcqscData,
                            explanation: e.target.value,
                          })
                        }
                        className="form-input"
                        rows={3}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Correct Answer Points</label>
                      <input
                        type="number"
                        value={mcqscData.correct_answer_points}
                        onChange={e =>
                          setMcqscData({
                            ...mcqscData,
                            correct_answer_points: e.target.value,
                          })
                        }
                        className="form-input"
                        placeholder="Enter points for correct answer"
                      />
                    </div>
                    <div className="form-group">
                      <label>Wrong Answer Points</label>
                      <input
                        type="number"
                        value={mcqscData.wrong_answer_points}
                        onChange={e =>
                          setMcqscData({
                            ...mcqscData,
                            wrong_answer_points: e.target.value,
                          })
                        }
                        className="form-input"
                        placeholder="Enter points for wrong answer"
                      />
                    </div>
                  </div>
                </div>
              )}

              {postForm.post_type === 'mcqmc' && (
                <div className="mcqmc-section">
                  <h3 className="section-title">MCQMC Question Details</h3>
                  <div className="form-group">
                    <label>
                      Question <span className="required">*</span>
                    </label>
                    <textarea
                      value={mcqmcData.question}
                      onChange={e =>
                        setMcqmcData({ ...mcqmcData, question: e.target.value })
                      }
                      className="form-input"
                      rows={3}
                    />
                  </div>
                  <div className="mcqmc-options-container">
                    {['A', 'B', 'C', 'D'].map(option => (
                      <div key={option} className="mcqmc-option-card">
                        <div className="option-header">
                          <label className="checkbox-item">
                            <input
                              type="checkbox"
                              checked={
                                mcqmcData.correctAnswers[
                                  option as keyof typeof mcqmcData.correctAnswers
                                ]
                              }
                              onChange={() =>
                                setMcqmcData({
                                  ...mcqmcData,
                                  correctAnswers: {
                                    ...mcqmcData.correctAnswers,
                                    [option]:
                                      !mcqmcData.correctAnswers[
                                        option as keyof typeof mcqmcData.correctAnswers
                                      ],
                                  },
                                })
                              }
                              className="checkbox-input"
                            />
                            <span className="checkbox-label">
                              Mark as Correct Answer
                            </span>
                          </label>
                        </div>
                        <div className="form-group">
                          <label>
                            Option {option} <span className="required">*</span>
                          </label>
                          <textarea
                            value={
                              mcqmcData[
                                `option${option}` as keyof typeof mcqmcData
                              ] as string
                            }
                            onChange={e =>
                              setMcqmcData({
                                ...mcqmcData,
                                [`option${option}`]: e.target.value,
                              })
                            }
                            className="form-input"
                            rows={2}
                          />
                        </div>
                        {mcqmcData.correctAnswers[
                          option as keyof typeof mcqmcData.correctAnswers
                        ] && (
                          <div className="form-group">
                            <label>
                              Explanation for Option {option}{' '}
                              <span className="required">*</span>
                            </label>
                            <textarea
                              value={
                                mcqmcData.explanations[
                                  option as keyof typeof mcqmcData.explanations
                                ]
                              }
                              onChange={e =>
                                setMcqmcData({
                                  ...mcqmcData,
                                  explanations: {
                                    ...mcqmcData.explanations,
                                    [option]: e.target.value,
                                  },
                                })
                              }
                              className="form-input"
                              rows={3}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Correct Answer Points</label>
                      <input
                        type="number"
                        value={mcqmcData.correct_answer_points}
                        onChange={e =>
                          setMcqmcData({
                            ...mcqmcData,
                            correct_answer_points: e.target.value,
                          })
                        }
                        className="form-input"
                        placeholder="Enter points for correct answer"
                      />
                    </div>
                    <div className="form-group">
                      <label>Wrong Answer Points</label>
                      <input
                        type="number"
                        value={mcqmcData.wrong_answer_points}
                        onChange={e =>
                          setMcqmcData({
                            ...mcqmcData,
                            wrong_answer_points: e.target.value,
                          })
                        }
                        className="form-input"
                        placeholder="Enter points for wrong answer"
                      />
                    </div>
                  </div>
                </div>
              )}

              {postForm.post_type === 'poll' && (
                <div className="mcqsc-section">
                  <h3 className="section-title">Poll Question Details</h3>
                  <div className="form-group">
                    <label>
                      Question <span className="required">*</span>
                    </label>
                    <textarea
                      value={mcqscData.question}
                      onChange={e =>
                        setMcqscData({ ...mcqscData, question: e.target.value })
                      }
                      className="form-input"
                      rows={3}
                    />
                  </div>
                  <div className="options-grid">
                    <div className="form-group">
                      <label>
                        Option A <span className="required">*</span>
                      </label>
                      <textarea
                        value={mcqscData.optionA}
                        onChange={e =>
                          setMcqscData({
                            ...mcqscData,
                            optionA: e.target.value,
                          })
                        }
                        className="form-input"
                        rows={2}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Option B <span className="required">*</span>
                      </label>
                      <textarea
                        value={mcqscData.optionB}
                        onChange={e =>
                          setMcqscData({
                            ...mcqscData,
                            optionB: e.target.value,
                          })
                        }
                        className="form-input"
                        rows={2}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Option C <span className="required">*</span>
                      </label>
                      <textarea
                        value={mcqscData.optionC}
                        onChange={e =>
                          setMcqscData({
                            ...mcqscData,
                            optionC: e.target.value,
                          })
                        }
                        className="form-input"
                        rows={2}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Option D <span className="required">*</span>
                      </label>
                      <textarea
                        value={mcqscData.optionD}
                        onChange={e =>
                          setMcqscData({
                            ...mcqscData,
                            optionD: e.target.value,
                          })
                        }
                        className="form-input"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              )}

              {postForm.post_type === 'qna' && (
                <div className="qna-section">
                  <h3 className="section-title">QNA Question Details</h3>
                  <div className="form-group">
                    <label>
                      Question <span className="required">*</span>
                    </label>
                    <textarea
                      value={qnaData.question}
                      onChange={e =>
                        setQnaData({ ...qnaData, question: e.target.value })
                      }
                      className="form-input"
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      Answer <span className="required">*</span>
                    </label>
                    <textarea
                      value={qnaData.answer}
                      onChange={e =>
                        setQnaData({ ...qnaData, answer: e.target.value })
                      }
                      className="form-input"
                      rows={4}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      Explanation <span className="required">*</span>
                    </label>
                    <textarea
                      value={qnaData.explanation}
                      onChange={e =>
                        setQnaData({ ...qnaData, explanation: e.target.value })
                      }
                      className="form-input"
                      rows={3}
                    />
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="post_image_url">Image URL</label>
                <input
                  type="url"
                  id="post_image_url"
                  value={postForm.image_url}
                  onChange={e =>
                    setPostForm({ ...postForm, image_url: e.target.value })
                  }
                  placeholder="https://example.com/image.jpg"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="link_url">Link URL</label>
                <input
                  type="url"
                  id="link_url"
                  value={postForm.link_url}
                  onChange={e =>
                    setPostForm({ ...postForm, link_url: e.target.value })
                  }
                  placeholder="https://example.com"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="thumbnail">Thumbnail URL</label>
                <input
                  type="url"
                  id="thumbnail"
                  value={postForm.thumbnail}
                  onChange={e =>
                    setPostForm({ ...postForm, thumbnail: e.target.value })
                  }
                  placeholder="https://example.com/thumb.jpg"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="post_video_url">Video URL</label>
                <input
                  type="url"
                  id="post_video_url"
                  value={postForm.video_url}
                  onChange={e =>
                    setPostForm({ ...postForm, video_url: e.target.value })
                  }
                  placeholder="https://example.com/video.mp4"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="youtube_url">YouTube URL</label>
                <input
                  type="url"
                  id="youtube_url"
                  value={postForm.youtube_url}
                  onChange={e =>
                    setPostForm({ ...postForm, youtube_url: e.target.value })
                  }
                  placeholder="https://youtube.com/watch?v=..."
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="audio_url">Audio URL</label>
                <input
                  type="url"
                  id="audio_url"
                  value={postForm.audio_url}
                  onChange={e =>
                    setPostForm({ ...postForm, audio_url: e.target.value })
                  }
                  placeholder="https://example.com/audio.mp3"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="lrc_url">LRC URL</label>
                <input
                  type="url"
                  id="lrc_url"
                  value={postForm.lrc_url}
                  onChange={e =>
                    setPostForm({ ...postForm, lrc_url: e.target.value })
                  }
                  placeholder="https://example.com/lyrics.lrc"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="post_language">Post Language</label>
                <select
                  id="post_language"
                  value={postForm.post_language}
                  onChange={e =>
                    setPostForm({ ...postForm, post_language: e.target.value })
                  }
                  className="form-input"
                >
                  <option value="en">English</option>
                  <option value="hindi">Hindi</option>
                  <option value="hinglish">Hinglish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  value={postForm.category}
                  onChange={e =>
                    setPostForm({ ...postForm, category: e.target.value })
                  }
                  className="form-input"
                >
                  <option value="">Select a category</option>
                  <option value="devotional">Devotional</option>
                  <option value="self-help">Self Help</option>
                  <option value="health">Health</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="correct_answer_points">
                  Correct Answer Points
                </label>
                <input
                  type="number"
                  id="correct_answer_points"
                  value={postForm.correct_answer_points}
                  onChange={e =>
                    setPostForm({
                      ...postForm,
                      correct_answer_points: e.target.value,
                    })
                  }
                  placeholder="Enter points for correct answer"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="wrong_answer_points">Wrong Answer Points</label>
                <input
                  type="number"
                  id="wrong_answer_points"
                  value={postForm.wrong_answer_points}
                  onChange={e =>
                    setPostForm({
                      ...postForm,
                      wrong_answer_points: e.target.value,
                    })
                  }
                  placeholder="Enter points for wrong answer"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="tags">Tags</label>
                <select
                  id="tags"
                  multiple
                  value={availableTags
                    .filter(tag => postForm.tags.includes(tag.slug))
                    .map(tag => tag.id)}
                  onChange={e => {
                    const selectedIds = Array.from(
                      e.target.selectedOptions,
                      option => option.value
                    );
                    const selectedSlugs = availableTags
                      .filter(tag => selectedIds.includes(tag.id))
                      .map(tag => tag.slug);
                    setPostForm({ ...postForm, tags: selectedSlugs });
                  }}
                  className="form-select"
                  size={5}
                >
                  {availableTags.map(tag => (
                    <option key={tag.id} value={tag.id}>
                      {tag.tag_name}
                    </option>
                  ))}
                </select>
                <span className="form-hint">
                  Hold Ctrl/Cmd to select multiple tags
                </span>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleClosePostModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSubmitPost}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div className="button-spinner"></div>
                    {editingPost ? 'Updating...' : 'Creating...'}
                  </>
                ) : editingPost ? (
                  'Update Post'
                ) : (
                  'Create Post'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTimeModal && (
        <div className="modal-overlay" onClick={() => setShowTimeModal(false)}>
          <div className="time-modal" onClick={e => e.stopPropagation()}>
            <div className="time-modal-header">
              <h3>Edit Timestamp</h3>
              <button
                className="modal-close"
                onClick={() => setShowTimeModal(false)}
              >
                ×
              </button>
            </div>
            <div className="time-modal-body">
              <input
                type="text"
                value={timeValue}
                onChange={e => setTimeValue(e.target.value)}
                className="time-input"
              />
            </div>
            <div className="time-modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowTimeModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={async () => {
                  try {
                    await authService.updatePostTime(
                      selectedPost.id,
                      timeValue
                    );
                    setShowTimeModal(false);
                    await fetchPosts();
                  } catch (error) {
                    console.error('Error updating timestamp:', error);
                  }
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showOrderModal && (
        <div className="modal-overlay" onClick={() => setShowOrderModal(false)}>
          <div className="order-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change Order</h2>
              <button
                className="modal-close"
                onClick={() => setShowOrderModal(false)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="order-modal-body">
              <div className="order-list">
                {orderList.map((post, index) => (
                  <div
                    key={post.id}
                    className={`order-item ${orderDraggedIndex === index ? 'dragging' : ''}`}
                    draggable
                    onDragStart={() => setOrderDraggedIndex(index)}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => {
                      e.preventDefault();
                      if (
                        orderDraggedIndex !== null &&
                        orderDraggedIndex !== index
                      ) {
                        const newOrder = [...orderList];
                        const draggedItem = newOrder[orderDraggedIndex];
                        newOrder.splice(orderDraggedIndex, 1);
                        newOrder.splice(index, 0, draggedItem);
                        setOrderList(newOrder);
                      }
                      setOrderDraggedIndex(null);
                    }}
                    onDragEnd={() => setOrderDraggedIndex(null)}
                  >
                    <span className="order-number">#{index + 1}</span>
                    <span className="order-title">
                      {post.attributes?.title || 'Untitled'}
                    </span>
                    <svg
                      className="drag-handle"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <line x1="3" y1="12" x2="21" y2="12" />
                      <line x1="3" y1="18" x2="21" y2="18" />
                    </svg>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowOrderModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={async () => {
                  try {
                    const postIds = orderList.map(post => post.id);
                    await authService.batchUpdatePostOrder(postIds);
                    await fetchPosts();
                    setShowOrderModal(false);
                    alert('Order saved successfully!');
                  } catch (error) {
                    console.error('Error saving order:', error);
                    alert('Failed to save order. Please try again.');
                  }
                }}
              >
                Save Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DCModuleDetailsPage;
